import { useEffect, useRef, useState } from 'react';
import useEmbedEvents from './useEmbedEvents';

const useBottomBarPosition = () => {
    const { iFramePagePosition } = useEmbedEvents();
    const { iFrameDistanceFromTop, parentDimensions } = iFramePagePosition;

    // The minimum space we want to container to take up.
    /**
     * The minimum space we want the container to take up.
     */
    const MIN_CONTAINER_SPACE = 80;

    /**
     * The padding at the bottom of the iframe to prevent a hidden page navigation.
     */
    const BOTTOM_PADDING = 70;

    const SCROLLING_START_OFFSET = 800;

    const DEBOUNCE_DELAY = 100; // ms

    // Use state to store the iframe height
    const [iFrameHeight, setIFrameHeight] = useState(0);

    // Set the iframe height on mount and when the window resizes
    useEffect(() => {
        function updateHeight() {
            setIFrameHeight(window.innerHeight);
        }
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    /**
     * Ensures that the scrolling only starts when the top of the iframe is reached.
     */
    const topOfIframeReached = iFrameDistanceFromTop < SCROLLING_START_OFFSET;

    /**
     * We invert the scrolling value because the iFrameDistanceFromTop is negative when the top of the iFrame is reached.
     * This means that the distance scrolled in the iFrame is positive when the top of the iFrame is reached.
     */
    const invertedIFrameDistanceFromTop = -iFrameDistanceFromTop;

    /**
     * The distance scrolled in the iFrame.
     */
    const distanceScrolledInIFrame = topOfIframeReached ? invertedIFrameDistanceFromTop : 0;

    /**
     * The remaining space below the IFrame
     */
    const spaceBelowIFrame = parentDimensions.height - iFrameHeight;

    /**
     * The position where the iFrame starts scrolling.
     * This is the height of the iFrame plus the space below it.
     */
    const bottomBarStartPosition = iFrameHeight + spaceBelowIFrame - MIN_CONTAINER_SPACE;

    const distanceFromTop = Math.max(
        bottomBarStartPosition - SCROLLING_START_OFFSET,
        /**
         * The distance scrolled in the iFrame plus the position where the bottom bar starts.
         * This ensures that the bottom bar is always at least at the bottom of the iFrame
         */
        distanceScrolledInIFrame + bottomBarStartPosition
    );

    const barDistanceFromTop = Math.min(
        distanceFromTop,

        /**
         * Ensure the bar doesn't go below the frame.
         */
        iFrameHeight - MIN_CONTAINER_SPACE - BOTTOM_PADDING
    );

    const barHiddenPosition = 200 + iFrameHeight;

    const [barTop, setBarTop] = useState(barDistanceFromTop);
    const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // When barDistanceFromTop changes, hide the bar immediately
        setBarTop(barHiddenPosition);

        // Then, after debounce, show it at the correct position
        if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(() => {
            setBarTop(barDistanceFromTop);
        }, DEBOUNCE_DELAY);

        return () => {
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
        };
    }, [barDistanceFromTop, barHiddenPosition]);

    return {
        topPosition: barTop,
        parentOptions: iFramePagePosition,
    };
};
export default useBottomBarPosition;
